import React, { useState, useContext, useEffect } from 'react';
import { ModalContext, IModalContext } from 'contexts/modal';
import { IUserContext, UserContext } from 'contexts/user';
import EmptyState from 'components/empty-state';
import useSizeClass from 'hooks/use-size-class';
import ButtonGroup from './button-group';
import PriceHistoryRow from './price-history-row';
import LoadingSpinner from 'components/loading-spinner';
import ArrowIcon from 'components/icon/arrow-icon';
import styles from './style.module.scss';
import { listingPageModalIds } from 'constants/test-constants';
import { useTranslation } from 'next-i18next';
import { SiteNameSpace } from 'constants/locale';

import type Listing from 'data/listing';

interface Props {
  listing: Listing;
  isVisible: boolean;
  listings: Listing[];
  priceHistoryRef?: React.RefObject<HTMLDivElement>;
  isModal?: boolean;
}

const NUMBER_OF_ROWS_BY_DEFAULT = 3;

export default function PriceHistory({ listing, isVisible, listings, priceHistoryRef, isModal = false }: Props) {
  const { isAuthenticated } = useContext(UserContext) as IUserContext;
  const { openModal } = useContext(ModalContext) as IModalContext;
  const [isRental, setIsRental] = useState(!!listing.isRental);
  const { isPrivate: listingIsPrivate, isCrea, isSignInAndTermsOfUseRequired, providerId } = listing;
  const isPrivate = !isAuthenticated && listingIsPrivate;
  const [showNumberOfListings, setShowNumberOfListings] = useState(isPrivate ? 1 : NUMBER_OF_ROWS_BY_DEFAULT);
  const isMobile = useSizeClass({ max: 1000 });
  const { t } = useTranslation(SiteNameSpace.Modal);

  const listingsFilteredByRentOrBuy = listings.filter(listing => listing.isRental === isRental);
  const containsRentalHistory = !!listings.filter(listing => listing.isRental !== isRental).length;

  useEffect(() => {
    setShowNumberOfListings(NUMBER_OF_ROWS_BY_DEFAULT);
  }, [isPrivate]);

  const toggleShowMoreSoldData = () => {
    if (showNumberOfListings > NUMBER_OF_ROWS_BY_DEFAULT) {
      setShowNumberOfListings(NUMBER_OF_ROWS_BY_DEFAULT);
    } else {
      setShowNumberOfListings(listings.length);
    }
  };

  const signIn = () => openModal('login-registration', { warningMessage: t('login-registration.form.signinRequiredWarning') });

  const openTermsModal = () => {
    if (!isVisible) {
      openModal('terms-of-use', { providerId });
    }
  };

  const renderFooter = () => {
    if (isPrivate || (isSignInAndTermsOfUseRequired && !isVisible && !isAuthenticated) || (!isAuthenticated && listingsFilteredByRentOrBuy.length > 1)) {
      return (
        <div className={styles['login-cta']}>
          Real estate boards require that you sign in to see the full details of this property. <span onClick={signIn}> Log in or sign up </span> to see price history.
        </div>
      );
    } else if (((isCrea || isSignInAndTermsOfUseRequired) && !isVisible) || (!isAuthenticated && listingsFilteredByRentOrBuy.length > 1)) {
      return (
        <div className={styles['login-cta']}>
          Real estate boards require that you accept Terms of use to see the full details of this property. <span onClick={openTermsModal}>Accept Terms of Use </span> to see price history.
        </div>
      );
    }

  };

  return (
    <div className={styles.component} data-testid={listingPageModalIds.priceHistorySection} ref={priceHistoryRef}>
      <h2 data-testid={listingPageModalIds.priceHistoryHeading}>
        Price History
      </h2>
      {!listingsFilteredByRentOrBuy.length && <EmptyState message='No History Available' />}
      {!listingsFilteredByRentOrBuy.length && <LoadingSpinner />}
      {containsRentalHistory && <ButtonGroup isRental={isRental} setIsRental={setIsRental} />}
      {!isMobile && !!listingsFilteredByRentOrBuy.length && <DesktopPriceHistoryHeader isRental={isRental} />}
      {listingsFilteredByRentOrBuy.slice(0, showNumberOfListings).map((currentListing, index) =>
        <PriceHistoryRow isVisible={isVisible} isAuthenticated={isAuthenticated} key={index} isMobile={isMobile} listing={listing} currentListing={currentListing} openModal={openModal} isPrivate={isPrivate} isModal={isModal} />
      )}
      {listingsFilteredByRentOrBuy.length > NUMBER_OF_ROWS_BY_DEFAULT && isVisible &&
        <button onClick={toggleShowMoreSoldData} className={`${styles['view-more']} ${showNumberOfListings > NUMBER_OF_ROWS_BY_DEFAULT && styles.less}`}>
          View {showNumberOfListings <= NUMBER_OF_ROWS_BY_DEFAULT ? 'More' : 'Less'} <ArrowIcon />
        </button>
      }
      {renderFooter()}
    </div>
  );
}

const DesktopPriceHistoryHeader = ({ isRental }: {isRental: boolean}) => {
  return (
    <div className={`${styles.grid} ${styles.header}`}>
      <div>List Date</div>
      <div>List Price</div>
      <div>Status</div>
      <div>End Date</div>
      <div>{isRental ? 'Rent' : 'Sold'} Price</div>
      <div></div>
    </div>
  );
};
